import { useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";
import { useAppMutation } from "../../../../../../../../http/useAppMutation.ts";
import { trackEvent } from "../../../../../../../../tracking/tracking.ts";
import {
  CHARACTER_MODEL_TYPE,
  OBJECT_MODEL_TYPE,
} from "../../../../../../../constants.ts";
import { useWorkspaceUuid } from "../../../../../../../hooks/useWorkspaceUuid.ts";
import { LORA_GENERATION_TYPE } from "../../../../../../constants.ts";
import { useBoardUuid } from "../../../../../../hooks/useBoardUuid.ts";
import { useUploadImage } from "../../../../../../hooks/useUploadImage.ts";
import {
  DEFAULT_GENERATIVE_FILL_MODEL,
  DIFFERENTIAL_DIFFUSION_GENERATIVE_FILL_MODEL,
} from "../constants.ts";
import { useGenerativeFillSettings } from "./useGenerativeFillSettings.ts";

export const useUploadAndGenerativeFillMutation = ({
  onSuccess,
}: {
  onSuccess?: () => void;
}) => {
  // XXX: We use refs to store user inputs and be able to use them in the second mutation (generation mutation) called
  // on successful upload.
  // FIXME: Replace this double mutation by a single chain of async mutations for better readability
  const generationAssetUuid = useRef<string>();

  const { mutate: generativeFill, isLoading: isGenerativeFillLoading } =
    useGenerativeFill({
      onSuccess: () => {
        onSuccess?.();
      },
    });

  const { mutate: upload, isLoading: isUploading } = useUploadImage({
    onSuccess: (imageUuid) => {
      if (generationAssetUuid.current) {
        generativeFill({
          assetUuid: generationAssetUuid.current,
          maskUuid: imageUuid,
        });
      }
    },
  });

  return {
    mutate: ({ mask, assetUuid }: { mask: Blob; assetUuid: string }) => {
      generationAssetUuid.current = assetUuid;
      upload({ image: mask });
    },
    isLoading: isGenerativeFillLoading || isUploading,
  };
};

const useGenerativeFill = ({ onSuccess }: { onSuccess?: () => void }) => {
  const workspaceUuid = useWorkspaceUuid();
  const boardUuid = useBoardUuid();
  const {
    mutation: { mutate, isLoading },
  } = useAppMutation({
    path: "boards/inpaint-image",
    invalidate: [
      `workspaces/${workspaceUuid}/boards`,
      `boards/${boardUuid}/assets`,
    ],

    onSuccess: () => {
      trackEvent(
        "content:generate",
        {
          generation_type: "Fill",
        },
        true,
      );
    },
  });

  const { settings } = useGenerativeFillSettings(boardUuid);

  const selectedElementsGenerationParams: typeof settings.selectedModels.generationParams =
    [];
  for (const modelGenerationParam of settings.selectedModels.generationParams) {
    settings.selectedModels.models
      .find((it) => it.uuid === modelGenerationParam.uuid)
      ?.let((model) => {
        if (
          model.type === OBJECT_MODEL_TYPE ||
          model.type === CHARACTER_MODEL_TYPE
        ) {
          selectedElementsGenerationParams.push(modelGenerationParam);
        }
      });
  }

  const queryClient = useQueryClient();

  const selectedElementGenerationParams =
    selectedElementsGenerationParams.length > 0
      ? selectedElementsGenerationParams[0]
      : null;

  return {
    mutate: ({
      maskUuid,
      assetUuid,
    }: {
      maskUuid: string;
      assetUuid: string;
    }) => {
      mutate(
        {
          prompt: settings.prompt,
          board_uuid: boardUuid,
          asset_uuid: assetUuid,
          mask_uuid: maskUuid,
          num_generations: settings.num_generations,
          guidance_scale: settings.guidance_scale,
          model_used: settings.model_used,
          ...(selectedElementGenerationParams &&
            selectedElementGenerationParams.model_generation_type ===
              LORA_GENERATION_TYPE && {
              element_uuid: selectedElementGenerationParams.uuid,
              lora_scale: selectedElementGenerationParams.scale,
            }),
          ...(settings.model_used ===
            DIFFERENTIAL_DIFFUSION_GENERATIVE_FILL_MODEL ||
          settings.model_used === DEFAULT_GENERATIVE_FILL_MODEL
            ? {
                differential_diffusion_settings: {
                  creativity_strength_preset:
                    settings.differential_diffusion_settings
                      .creativity_strength_preset,
                  prompt_strength:
                    settings.differential_diffusion_settings
                      .creativity_strength_preset === "CUSTOM"
                      ? settings.differential_diffusion_settings.prompt_strength
                      : null,
                },
              }
            : {
                differential_diffusion_settings: null,
              }),
        },
        {
          onSuccess: () => {
            [`assets/${assetUuid}`, `assets/${assetUuid}/history`].map(
              (queryKey) =>
                void queryClient.invalidateQueries({ queryKey: [queryKey] }),
            );
            onSuccess?.();
          },
        },
      );
    },
    isLoading,
  };
};
